import { AxiosInstance } from "axios";
import { aggregatePagedResponse, aggregatePagedResponseLists } from "@iventis/api-helpers/src/api-helpers";
import { getAssetsByIds } from "@iventis/api-helpers/src/assets";
import { getAssetSignature } from "@iventis/utilities/src/performance/asset-signature-cache";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import type { Relay } from "@iventis/domain-model/model/relay";
import type { RelayDayDetail } from "@iventis/domain-model/model/relayDayDetail";
import type { RelayStage } from "@iventis/domain-model/model/relayStage";
import type { RelayStreamDetail } from "@iventis/domain-model/model/relayStreamDetail";
import type { RelayTimetableOrderMapObjectPatch } from "../converters/relay-timetable-order-converters";

/** The relay an open day belongs to, along with the stream the open map is */
export interface RelayTimetableRelayDay {
    relay: Relay;
    stream: RelayStreamDetail;
}

/**
 * The icon of each layer template keyed by the template's id, and how long the urls are good for.
 *
 * The urls carry a signature which lapses some time after the read, so the icons are read again before then.
 */
export interface RelayTimetableLayerIconUrls {
    iconUrlsByTemplateId: Record<string, string | null>;
    /** The seconds the signatures on the urls are good for, or null when the urls carry no signature and never lapse */
    signatureExpirySeconds: number | null;
}

/** Everything the timetable reads from and writes to the backend, so a consumer without the mapping api can supply its own */
export interface RelayTimetableApiFunctions {
    getRelayDayForMap(relayId: string, dayId: string, mapId: string): Promise<RelayTimetableRelayDay | undefined>;
    getRelayTimetableMapObjects(mapId: string, layerIds: string[], signal?: AbortSignal): Promise<MapObject[]>;
    /** Every list item of an attribute, which is what the speeds a route can be travelled at are held as */
    getDataFieldListItems(dataFieldId: string, signal?: AbortSignal): Promise<DataFieldListItem[]>;
    /** Saves where a stage sits on its stream, which is what a drag of a stage in the timetable changes */
    updateRelayStageOrder(relayId: string, stageId: string, order: number): Promise<void>;
    /** Saves what a stage is called, which is what renaming a stage row in the timetable changes */
    updateRelayStageName(relayId: string, stageId: string, name: string): Promise<void>;
    /**
     * Creates a stage on a stream of the relay at the given place on its line, and hands back the stage it created,
     * which is what the create stage button in the timetable does
     */
    createRelayStage(relayId: string, streamId: string, name: string, order: number): Promise<RelayStage>;
    /** Deletes a stage, which takes the layers inside it and the objects drawn on them with it */
    deleteRelayStage(relayId: string, stageId: string): Promise<void>;
    /** The icon of each of the given layer templates, which is what the points drawn on those layers are marked with */
    getLayerTemplateIconUrls(templateIds: string[], signal?: AbortSignal): Promise<RelayTimetableLayerIconUrls>;
    /** Saves attribute values straight to the api, for writes the map need not know about such as filling in a missing order */
    patchMapObjectsDataFieldValues(mapId: string, patches: RelayTimetableOrderMapObjectPatch[]): Promise<void>;
}

export function createRelayTimetableApiFunctions(mappingApi: AxiosInstance, assetsApi: AxiosInstance): RelayTimetableApiFunctions {
    return {
        /**
         * Gets the relay for the open day along with the stream the open map is, which is what checks that the map
         * belongs to that day. A stream is a plan of its own, so the open map is one stream of the day or none of them.
         */
        async getRelayDayForMap(relayId, dayId, mapId) {
            const [{ data: relays }, { data: day }] = await Promise.all([mappingApi.get<Relay[]>("/relays"), mappingApi.get<RelayDayDetail>(`/relays/${relayId}/days/${dayId}`)]);

            const stream = day.streams?.find(({ id }) => id === mapId);
            const relay = relays.find(({ id }) => id === relayId);

            return stream == null || relay == null ? undefined : { relay, stream };
        },

        async updateRelayStageOrder(relayId, stageId, order) {
            await mappingApi.patch(`/relays/${relayId}/stages/${stageId}`, { order });
        },

        async updateRelayStageName(relayId, stageId, name) {
            await mappingApi.patch(`/relays/${relayId}/stages/${stageId}`, { name });
        },

        async createRelayStage(relayId, streamId, name, order) {
            const { data } = await mappingApi.post<RelayStage>(`/relays/${relayId}/stages`, { streamId, name, order });

            return data;
        },

        async deleteRelayStage(relayId, stageId) {
            await mappingApi.delete(`/relays/${relayId}/stages/${stageId}`);
        },

        /**
         * Gets every map object on the given layers, which for the relay timetable is every layer of the day being shown.
         *
         * All of them are needed up front because the timetable calculates its times on the frontend.
         */
        async getRelayTimetableMapObjects(mapId, layerIds, signal) {
            if (layerIds.length === 0) {
                return [];
            }

            const config = {
                params: {
                    mapId,
                    filter: JSON.stringify([{ $type: "IventisFilterGuidArray", fieldName: "layerId", operator: IventisFilterOperator.In, value: layerIds }]),
                },
                signal,
            };

            return aggregatePagedResponse<MapObject[]>(mappingApi, `/maps/${mapId}/map_objects/filter`, config, async (cum, newValue) => (cum ?? []).concat(newValue));
        },

        /**
         * Gets the icon of each layer template, which is the same asset the sidebar shows as a layer's preview.
         *
         * A layer template is itself an asset, so its id is the id of the asset holding its icon. A template whose asset
         * cannot be read is left out rather than failing the read, which leaves its segments unmarked.
         */
        async getLayerTemplateIconUrls(templateIds, signal) {
            const { found } = await getAssetsByIds(templateIds, assetsApi, signal);

            const signed = found.filter(({ iconUrl, authoritySignatureExpiry }) => iconUrl != null && authoritySignatureExpiry > 0);
            const shortestSignature = signed.reduce((shortest, asset) => Math.min(shortest, asset.authoritySignatureExpiry), Infinity);

            return {
                iconUrlsByTemplateId: Object.fromEntries(
                    found.filter(({ iconUrl }) => iconUrl != null).map((asset) => [asset.id, getAssetSignature(asset.iconUrl, asset.authoritySignature)])
                ),
                signatureExpirySeconds: signed.length > 0 ? shortestSignature : null,
            };
        },

        /**
         * Saves attribute values against the given map objects without going through the map's own state.
         *
         * An order is a number which is not drawn, so the map has no reason to hold the objects locally the way it does
         * for an edit made in the timetable.
         */
        async patchMapObjectsDataFieldValues(mapId, patches) {
            await mappingApi.patch(`/maps/${mapId}/map_objects/data-fields`, patches);
        },

        async getDataFieldListItems(dataFieldId, signal) {
            return aggregatePagedResponseLists<DataFieldListItem>(async (pageNumber) => {
                const response = await mappingApi.get<PagedResult<DataFieldListItem[]>>(`/data-fields/${dataFieldId}/list-items/filter`, { params: { pageNumber }, signal });
                return response.data;
            }, signal);
        },
    };
}
